import React from 'react';
import { Link } from 'react-router-dom';
import { FadeIn } from '../components/FadeIn';
const principles = [{
  title: 'Judgment is a Public Good',
  desc: 'The quality of mind guiding public life is not a private matter. It directly affects institutional continuity, public trust, and national resilience.'
}, {
  title: 'Authority Must Be Earned',
  desc: 'High civic responsibility must be tied to demonstrated competence, not merely to popularity, inheritance, wealth, or performance.'
}, {
  title: 'Citizenship is Developmental',
  desc: 'Citizens are not fixed categories. The constitutional order must provide pathways for growth, recalibration, and upward civic mobility.'
}, {
  title: 'Leadership is Stewardship',
  desc: 'To lead is not to dominate, but to bear consequence. Leadership is a burden of discipline, responsibility, and constitutional seriousness.'
}, {
  title: 'Power Must Be Restrained',
  desc: 'Any system built around competence must still remain bounded by law, oversight, and review.'
}];
const sections = [{
  number: '01',
  heading: 'Purpose of the Charter',
  paragraphs: ["The Charter exists to define the core political commitments of Cognarchism before they are expanded into detailed constitutional structures, laws, and institutions. It establishes the doctrine's moral center, its public claims, and its view of authority, citizenship, and institutional order.", 'At its foundation, the Charter argues that no society can remain stable when power is detached from judgment. Political systems that ignore meaningful differences in self-command, competence, and manipulation resistance do not become just; they become fragile.']
}, {
  number: '02',
  heading: 'First Principles',
  paragraphs: ['The Charter of Cognarchism rests on a set of first principles that structure the entire doctrine.'],
  principlesList: true
}, {
  number: '03',
  heading: 'The Civic Order It Envisions',
  paragraphs: ['The Charter outlines a civic order in which all citizens retain legal dignity, due process, and developmental opportunity, while higher levels of public authority require greater burdens of proof. The framework is not intended to create a hereditary ruling caste or a society of permanent political inferiors. Its purpose is to align responsibility with readiness.', 'This means that high-trust roles in law, security, constitutional stewardship, finance, and institutional leadership are treated differently from ordinary social participation. The more power a role carries, the more serious the standards required to hold it.']
}, {
  number: '04',
  heading: 'The Role of Development',
  paragraphs: ['A defining principle of the Charter is that the State must not merely sort people. It must develop them. Cognarchism rejects the idea that low civic readiness is grounds for permanent exclusion. Instead, it proposes that the constitutional order invest in strengthening reasoning, emotional regulation, social discipline, and resistance to manipulation across the population.', 'The purpose of development is both ethical and strategic. It affirms the possibility of human growth, while also strengthening the resilience of the larger political order.']
}, {
  number: '05',
  heading: 'Why the Charter Matters',
  paragraphs: ['The Charter matters because it provides the foundation for everything that follows: the constitutional articles, the NCJI framework, the developmental model of citizenship, and the doctrine of earned leadership. It is the bridge between political philosophy and state design.', 'Without the Charter, Cognarchism would remain a loose theory. With it, the project takes the form of a coherent political doctrine: one that can be translated into institutions, tested in public argument, and structured into constitutional law.']
}, {
  number: '06',
  heading: 'Core Declaration',
  paragraphs: ['At the heart of the Charter is a single civilizational claim: modern political systems will continue to fail until they take judgment seriously enough to build around it.', 'Cognarchism therefore begins from a harder standard. It asks more of citizens, more of institutions, and far more of those who seek to govern. It treats competence not as a luxury, but as the precondition of durable order.']
}];
export function Charter() {
  return <article data-id="element-187">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-cognarch-white border-b border-cognarch-border" data-id="element-188">
        <div className="max-w-3xl mx-auto px-6" data-id="element-189">
          <FadeIn delay={0.1} direction="down" data-id="element-190">
            <p className="uppercase tracking-[0.25em] text-xs font-medium text-cognarch-gray mb-8" data-id="element-191">
              Founding Text
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-192">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-serif font-medium leading-[1.1] tracking-tight text-cognarch-black mb-8 text-balance" data-id="element-193">
              The founding charter of a judgment-centered political order
            </h1>
          </FadeIn>

          <FadeIn delay={0.4} data-id="element-194">
            <p className="text-lg md:text-xl text-cognarch-gray leading-relaxed mb-12 max-w-2xl" data-id="element-195">
              The Charter of Cognarchism sets out the first principles of a
              constitutional system built around civic development, earned
              authority, lawful restraint, and the protection of judgment as a
              public good.
            </p>
          </FadeIn>

          <FadeIn delay={0.5} className="flex flex-col sm:flex-row gap-4" data-id="element-196">
            <Link to="#" className="inline-block px-7 py-3.5 bg-cognarch-black text-white font-medium tracking-wide hover:bg-cognarch-charcoal transition-all duration-300 text-sm text-center" data-id="element-197">
              Read the Constitution
            </Link>
            <Link to="#" className="inline-block px-7 py-3.5 border border-cognarch-charcoal text-cognarch-charcoal font-medium tracking-wide hover:bg-cognarch-charcoal hover:text-white transition-all duration-300 text-sm text-center" data-id="element-198">
              Explore the Manifesto
            </Link>
          </FadeIn>
        </div>
      </section>

      {/* Body Sections */}
      {sections.map((section, index) => <section key={section.number} className={`py-20 md:py-28 border-b border-cognarch-border ${index % 2 === 1 ? 'bg-cognarch-offwhite' : 'bg-cognarch-white'}`} data-id="element-199">
          <div className="max-w-3xl mx-auto px-6" data-id="element-200">
            <FadeIn data-id="element-201">
              <div className="flex items-baseline gap-6 mb-8" data-id="element-202">
                <span className="text-cognarch-border font-serif text-4xl md:text-5xl leading-none select-none" data-id="element-203">
                  {section.number}
                </span>
                <h2 className="text-2xl md:text-4xl font-serif text-cognarch-charcoal leading-tight" data-id="element-204">
                  {section.heading}
                </h2>
              </div>
            </FadeIn>

            <FadeIn delay={0.1} data-id="element-205">
              <div className="space-y-6" data-id="element-206">
                {section.paragraphs.map((paragraph, pIndex) => <p key={pIndex} className="text-cognarch-gray leading-[1.8] text-base md:text-lg" data-id="element-207">
                    {paragraph}
                  </p>)}

                {section.principlesList && <div className="space-y-8 py-4 border-t border-cognarch-border mt-8 pt-8" data-id="element-208">
                    {principles.map((principle, pIndex) => <div key={pIndex} className="flex gap-4" data-id="element-209">
                        <span className="text-cognarch-charcoal/30 text-xs mt-1.5 shrink-0" data-id="element-210">
                          ◆
                        </span>
                        <div data-id="element-211">
                          <h3 className="text-cognarch-charcoal font-serif text-lg md:text-xl mb-2" data-id="element-212">
                            {principle.title}
                          </h3>
                          <p className="text-cognarch-gray leading-[1.8] text-base" data-id="element-213">
                            {principle.desc}
                          </p>
                        </div>
                      </div>)}
                  </div>}
              </div>
            </FadeIn>
          </div>
        </section>)}

      {/* Closing Statement Block */}
      <section className="py-24 md:py-32 bg-cognarch-black text-white" data-id="element-214">
        <div className="max-w-3xl mx-auto px-6 text-center" data-id="element-215">
          <FadeIn data-id="element-216">
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-217" />
            <p className="text-xl md:text-2xl font-serif leading-relaxed text-white/80 mb-12 text-balance" data-id="element-218">
              The Charter of Cognarchism defines the moral and constitutional
              logic of a political system in which authority is earned, citizens
              are developed, and judgment is protected as a condition of public
              continuity.
            </p>
            <div className="w-16 h-px bg-white/20 mx-auto mb-12" data-id="element-219" />
            <p className="uppercase tracking-[0.25em] text-sm text-white/50 font-medium" data-id="element-220">
              A charter for earned authority and civic elevation.
            </p>
          </FadeIn>
        </div>
      </section>
    </article>;
}